import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts as staticPosts } from '../data/blogPosts';
import { useSEO } from '../hooks/useSEO';
import FooterSection from '../components/landing/FooterSection';

interface ApiBlogPost {
  id: string;
  slug: string;
  title: string;
  description: string;
  cover_emoji: string;
  category: string;
  reading_time: string;
  author: string;
  tags: string[];
  published_at: string;
}

// REACT_APP_API_URL is e.g. "http://localhost:8000/api" — strip trailing /api for base
const _rawApiUrl = process.env.REACT_APP_API_URL || 'https://api.retrosive.com/api';
const API_BASE = _rawApiUrl.replace(/\/api\/?$/, '');

const BlogPage: React.FC = () => {
  useSEO({
    title: 'Blog — Agile Retrospective Tips & Guides',
    description:
      'Practical guides, comparisons, and tips for running better agile retrospectives. Learn about retrospective tools, templates, and best practices for scrum teams.',
    canonical: '/blog',
  });

  const [apiPosts, setApiPosts] = useState<ApiBlogPost[] | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/blog/posts/`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setApiPosts(data))
      .catch(() => setApiPosts(null));
  }, []);

  // Merge: API posts take priority, then fill with static posts not already covered
  const posts: Array<{ slug: string; title: string; description: string; coverEmoji: string; category: string; readingTime: string; author: string; publishedAt: string }> = (() => {
    if (apiPosts !== null) {
      const apiSlugs = new Set(apiPosts.map(p => p.slug));
      const fromStatic = staticPosts
        .filter(p => !apiSlugs.has(p.slug))
        .map(p => ({
          slug: p.slug,
          title: p.title,
          description: p.description,
          coverEmoji: p.coverEmoji,
          category: p.category,
          readingTime: String(p.readingTime),
          author: p.author,
          publishedAt: p.publishedAt,
        }));
      const fromApi = apiPosts.map(p => ({
        slug: p.slug,
        title: p.title,
        description: p.description,
        coverEmoji: p.cover_emoji,
        category: p.category,
        readingTime: p.reading_time,
        author: p.author,
        publishedAt: p.published_at,
      }));
      return [...fromApi, ...fromStatic].sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      );
    }
    return staticPosts
      .map(p => ({
        slug: p.slug,
        title: p.title,
        description: p.description,
        coverEmoji: p.coverEmoji,
        category: p.category,
        readingTime: String(p.readingTime),
        author: p.author,
        publishedAt: p.publishedAt,
      }))
      .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  })();

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-orange-800 to-slate-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <span className="inline-block px-3 py-1 bg-orange-500/20 text-orange-300 rounded-full text-sm font-medium mb-4">
            Retrosive Blog
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Retrospective Tips & Guides
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Practical advice for agile teams — tools, templates, and techniques to run retrospectives that actually drive improvement.
          </p>
        </div>
      </section>

      {/* Tool comparisons */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10">
        <Link
          to="/alternatives"
          className="block p-5 border border-orange-200 bg-orange-50 rounded-2xl hover:border-orange-300 hover:shadow-sm transition-all"
        >
          <p className="text-sm font-semibold text-orange-700 mb-1">Compare tools</p>
          <p className="text-slate-800 font-medium">
            Retrosive vs EasyRetro, Parabol, Ludi, Miro, and more →
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Honest feature comparisons to help you choose the right retrospective platform.
          </p>
        </Link>
      </section>

      {/* Posts grid */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group flex flex-col bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-orange-200 transition-all duration-200"
            >
              {/* Card header */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-8 flex items-center justify-center text-6xl">
                {post.coverEmoji}
              </div>

              <div className="flex flex-col flex-1 p-6">
                {/* Category + reading time */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-semibold text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full">
                    {post.category}
                  </span>
                  <span className="text-xs text-gray-400">{post.readingTime} min read</span>
                </div>

                <h2 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors leading-snug">
                  {post.title}
                </h2>
                <p className="text-sm text-gray-500 flex-1 line-clamp-3">{post.description}</p>

                <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                  <span>{post.author}</span>
                  <span>{formatDate(post.publishedAt)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-50 border-t border-gray-200 py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Ready to run better retrospectives?
          </h2>
          <p className="text-gray-500 mb-6">
            Retrosive is free to start — no credit card required.
          </p>
          <Link
            to="/register"
            className="inline-block px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors"
          >
            Get Started Free
          </Link>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default BlogPage;
